import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert
} from '@mui/material';
import {
  Cancel as CancelIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';

const RejectBookingDialog = ({ 
  open, 
  onClose, 
  booking, 
  onConfirmReject 
}) => {
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      return;
    }

    setLoading(true);
    try {
      await onConfirmReject(booking.id, rejectionReason.trim());
      handleClose();
    } catch (error) {
      console.error('Error rejecting booking:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRejectionReason('');
    setLoading(false);
    onClose();
  };

  if (!booking) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CancelIcon sx={{ color: '#dc3545' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Refuser la réservation
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ px: 3 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Vous êtes sur le point de refuser cette demande de réservation. 
            Le client recevra un email avec votre motif de refus.
          </Typography>
        </Alert>

        <Box sx={{ mb: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Détails de la réservation
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Client :</strong> {booking.clientName}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Service :</strong> {booking.service}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Date :</strong> {dayjs(booking.date).format('dddd DD MMMM YYYY')}
          </Typography>
          <Typography variant="body2">
            <strong>Heure :</strong> {dayjs(booking.date).format('HH:mm')}
          </Typography>
        </Box>

        <TextField
          fullWidth
          label="Motif du refus *"
          multiline
          rows={4}
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          placeholder="Expliquez pourquoi vous ne pouvez pas accepter cette réservation (ex: créneau non disponible, service non proposé, etc.)"
          required
          error={!rejectionReason.trim() && rejectionReason !== ''}
          helperText={
            !rejectionReason.trim() && rejectionReason !== '' 
              ? "Le motif du refus est obligatoire" 
              : "Ce message sera envoyé au client par email"
          }
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
        />
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button 
          onClick={handleClose} 
          sx={{ color: '#6B7280' }}
          disabled={loading}
        >
          Annuler
        </Button>
        <Button 
          onClick={handleReject} 
          variant="contained"
          disabled={!rejectionReason.trim() || loading}
          sx={{
            backgroundColor: '#dc3545',
            color: '#FFFFFF',
            '&:hover': { backgroundColor: '#c82333' },
            '&:disabled': { 
              backgroundColor: '#f8d7da',
              color: '#721c24'
            }
          }}
        >
          {loading ? 'Refus en cours...' : 'Confirmer le refus'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RejectBookingDialog;